import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import LoginForm from './components/LoginForm';
import TrustSignals from './components/TrustSignals';
import ComplianceInfo from './components/ComplianceInfo';
import Icon from '../../components/AppIcon';

const LoginPage = () => {
  const [user, setUser] = useState(null);

  const handleLogin = (userData) => {
    setUser(userData);
    // Store user data in localStorage for persistence
    localStorage.setItem('user', JSON.stringify(userData));
  };

  const handleLogout = () => {
    setUser(null);
    localStorage.removeItem('user');
  };

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>Login - MoneyTransfer Pro | Secure International Money Transfers</title>
        <meta name="description" content="Sign in to your MoneyTransfer Pro account for secure, regulated international money transfers with real-time rates and compliance tracking." />
        <meta name="keywords" content="login, money transfer, international payments, secure banking, financial services" />
      </Helmet>
      <Header user={user} onLogout={handleLogout} />
      <main className="container mx-auto px-4 py-8 lg:py-12">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 lg:gap-12">
          {/* Login Form - Main Content */}
          <div className="lg:col-span-1 order-1 lg:order-1">
            <LoginForm onLogin={handleLogin} />
          </div>

          {/* Trust Signals - Desktop Right, Mobile Second */}
          <div className="lg:col-span-1 order-2 lg:order-2">
            <div className="lg:sticky lg:top-24">
              <TrustSignals />
            </div>
          </div>

          {/* Compliance Information - Desktop Right, Mobile Third */}
          <div className="lg:col-span-1 order-3 lg:order-3">
            <div className="lg:sticky lg:top-24">
              <ComplianceInfo />
            </div>
          </div>
        </div>

        {/* Additional Security Information */}
        <div className="mt-12 bg-card rounded-lg border border-border p-8">
          <div className="text-center mb-8">
            <Icon name="Shield" size={48} className="text-primary mx-auto mb-4" />
            <h2 className="text-2xl font-semibold text-foreground mb-2">Enterprise-Grade Security</h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Your financial data and transactions are protected by the same security standards used by major banks and financial institutions worldwide.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="text-center">
              <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Icon name="Lock" size={24} className="text-primary" />
              </div>
              <h3 className="font-semibold text-foreground mb-2">End-to-End Encryption</h3>
              <p className="text-sm text-muted-foreground">All data encrypted in transit and at rest</p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Icon name="Eye" size={24} className="text-primary" />
              </div>
              <h3 className="font-semibold text-foreground mb-2">24/7 Monitoring</h3>
              <p className="text-sm text-muted-foreground">Continuous fraud detection and prevention</p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Icon name="UserCheck" size={24} className="text-primary" />
              </div>
              <h3 className="font-semibold text-foreground mb-2">Identity Verification</h3>
              <p className="text-sm text-muted-foreground">Multi-layer KYC and AML compliance</p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Icon name="Building" size={24} className="text-primary" />
              </div>
              <h3 className="font-semibold text-foreground mb-2">Regulatory Compliance</h3>
              <p className="text-sm text-muted-foreground">FCA regulated and internationally compliant</p>
            </div>
          </div>
        </div>

        {/* Footer Information */}
        <div className="mt-12 text-center">
          <div className="flex flex-wrap justify-center items-center space-x-6 text-sm text-muted-foreground">
            <span>© {new Date()?.getFullYear()} MoneyTransfer Pro</span>
            <span>•</span>
            <button className="hover:text-foreground transition-colors">Privacy Policy</button>
            <span>•</span>
            <button className="hover:text-foreground transition-colors">Terms of Service</button>
            <span>•</span>
            <button className="hover:text-foreground transition-colors">Compliance</button>
            <span>•</span>
            <button className="hover:text-foreground transition-colors">Contact Support</button>
          </div>
        </div>
      </main>
    </div>
  );
};

export default LoginPage;